import React, { useEffect, useState } from 'react';
import { Search, UserPlus, X, Loader2, CheckCircle2, AtSign, Handshake, Users } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTradeRecipient } from '../hooks/useTradeRecipient';
import { toast } from '../hooks/use-toast';

export default function UserSearchBar() {
  const { isAuthed, openAuth } = useAuth();
  const { recipient, setRecipient, clear } = useTradeRecipient();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAuthed) return;
    const cleaned = q.trim();
    if (!cleaned) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const list = await api.searchUsers(cleaned);
        setResults(list);
        setOpen(true);
      } catch { setResults([]); } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, isAuthed]);

  const pick = (u) => {
    setRecipient(u);
    setQ('');
    setResults([]);
    setOpen(false);
    toast({
      title: `Selected @${u.username}`,
      description: 'Now click any sprite below to send them a trade request.',
    });
  };

  return (
    <section
      className="max-w-7xl mx-auto px-3 sm:px-6 mt-4 sm:mt-5 pb-4"
      data-testid="user-search-bar"
    >
      <div
        className="rounded-2xl border-[3px] border-[#3a1064] shadow-[0_4px_0_#3a1064]"
        style={{ background: 'linear-gradient(135deg,#fff7d6 0%,#ffd900 100%)' }}
      >
        <div className="px-4 sm:px-5 py-4 flex items-center gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-xl bg-white border-2 border-[#3a1064] flex items-center justify-center shrink-0 shadow-[0_2px_0_#3a1064]">
            <Users className="h-5 w-5 text-[#ff2bd6]" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70">
              Find a trader
            </div>
            <div className="font-black text-[#3a1064] text-sm sm:text-base">
              Search a Sprite Vault user to trade with
            </div>
          </div>

          {recipient && (
            <div
              data-testid="selected-recipient-chip"
              className="flex items-center gap-2 bg-emerald-100 border-2 border-emerald-500 rounded-full px-3 py-1.5 shadow-[0_2px_0_#3a1064]"
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
              <span className="font-black text-emerald-800 text-xs">
                Trading with <span className="text-emerald-900">@{recipient.username}</span>
              </span>
              <button
                data-testid="clear-recipient-btn"
                onClick={clear}
                className="h-5 w-5 rounded-full bg-white border-2 border-[#3a1064] hover:bg-rose-100 flex items-center justify-center text-[#3a1064]"
                aria-label="Clear selected trader"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <div className="relative px-4 sm:px-5 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]/60 pointer-events-none" />
            <input
              data-testid="user-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder={isAuthed ? 'Type a username or name…' : 'Sign in to search traders'}
              disabled={!isAuthed}
              className="w-full h-11 pl-10 pr-10 rounded-xl border-2 border-[#3a1064] bg-white font-bold text-[#3a1064] placeholder:text-[#3a1064]/40 disabled:opacity-60 disabled:cursor-not-allowed"
            />
            {loading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]/60 animate-spin" />
            )}
            {!isAuthed && (
              <button
                data-testid="search-signin-btn"
                onClick={() => openAuth('login')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-3 rounded-lg border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs"
              >
                Sign in
              </button>
            )}
          </div>

          {open && q.trim() && (
            <div
              data-testid="user-search-results"
              className="absolute z-30 left-4 right-4 sm:left-5 sm:right-5 mt-2 bg-white border-2 border-[#3a1064] rounded-xl shadow-[0_4px_0_#3a1064] overflow-hidden max-h-[320px] overflow-y-auto"
            >
              {results.length === 0 && !loading ? (
                <div className="px-3 py-4 text-center text-sm font-bold text-[#3a1064]/70">
                  No traders match. Try a shorter handle.
                </div>
              ) : (
                results.map((u) => (
                  <button
                    key={u.id}
                    data-testid={`user-result-${u.username}`}
                    onClick={() => pick(u)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-yellow-50 border-b border-[#3a1064]/10 last:border-b-0 transition-colors"
                  >
                    <div className="h-9 w-9 rounded-full bg-[#ff2bd6] border-2 border-[#3a1064] flex items-center justify-center text-white font-black shrink-0 overflow-hidden">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="h-full w-full object-cover" />
                      ) : (
                        (u.name || u.username || '?').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-[#3a1064] text-sm truncate">{u.name || u.username}</div>
                      <div className="text-[11px] font-bold text-[#3a1064]/60 flex items-center gap-1">
                        <AtSign className="h-3 w-3" />{u.username}
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-[#ff2bd6] flex items-center gap-1 shrink-0">
                      <Handshake className="h-3 w-3" /> Trade
                    </span>
                  </button>
                ))
              )}
            </div>
          )}

          {!q.trim() && recipient && (
            <div className="mt-2 text-xs font-bold text-[#3a1064]/70 flex items-center gap-1.5">
              <UserPlus className="h-3.5 w-3.5" />
              Click any sprite below to send <span className="text-[#3a1064]">@{recipient.username}</span> a trade request.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
